import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {ColumnFormats} from "../../services/table-utils.service";
import {FormControl} from "@angular/forms";
import {MatSelect, MatSelectTrigger} from "@angular/material/select";
import {OptionListSearchComponent} from "../option-list-search/option-list-search.component";
import {COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import {FitContentDirective} from "../../directives/fit-content.directive";
import {SharedPipesModule} from "../../shared/modules/shared-pipes.module";
import {allStringFilter, isGuid} from "../../lib/utils";
import {isInteger} from "lodash-es";
import {CommonModule} from "@angular/common";
import {findOrphanedOption, OrphanedOption} from "../../lib/orphaned-option";

@Component({
    selector: 'select-many-filter',
    templateUrl: './select-many-filter.component.html',
    styleUrls: ['./select-many-filter.component.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        COMMON_FORM_IMPORTS,
        CommonModule,
        FitContentDirective,
        OptionListSearchComponent,
        MatSelectTrigger,
        SharedPipesModule
    ],
})

export class SelectManyFilterComponent implements OnInit {
    @ViewChild('filterSelect') filterSelect: MatSelect;
    @ViewChild(OptionListSearchComponent) searchComponent!: OptionListSearchComponent;

    filterValue: string = "";
    value: FormControl;
    filteredOptions: any;
    errorMessage: string;
    orphanedOptions: OrphanedOption[] = [];

    private isOpen = false;
    private _modelValue: any;

    get modelValue(): any {
        return this._modelValue;
    }

    @Input() set modelValue(value: any) {
        this._modelValue = value;
        this.value = new FormControl({value: this.modelValue, disabled: this.disabled});
        this.updateOrphanedOptions();
        this.changeDetectorRef.markForCheck();
    }

    @Input() listType: string;
    /**A string of classes to add to mat-form-field**/
    @Input() classes: string;
    @Input() filter_compare?: any;
    @Input() app_fit_content: any;
    @Input() conditional_formats: any;
    @Input() formats: ColumnFormats;
    @Input() panelClass: string;
    private _disabled = false;

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }

    set disabled(value: boolean) {
        this._disabled = value;
        if (!this.value) return;
        if (value === true) {
            this.value.disable();
        } else {
            this.value.enable();
        }
        this.changeDetectorRef.markForCheck();
    }

    @Input()
    stringFunction: (obj: any, other?: any) => string = (obj) => {
        if (!obj) return "";
        if (obj.name && obj.value) return obj.name;
        return obj?.attributes?.name || obj;
    }

    @Input()
    valueFunction = (obj: any, other?: any) => {
        if (!obj) return "";
        if (obj.name && obj.value) return obj.value;
        return obj?.id || obj;
    }

    @Input()
    disableItemFunction: (obj: any) => boolean = (obj) => {
        return false;
    }
    @Input() label: string;
    private _options: any[];
    @Input() set options(options: any[]) {
        this._options = options || [];
        this.filteredOptions = this._options;
        this.updateOrphanedOptions();
    };

    get options(): any[] {
        return this._options;
    }

    @Input() allow_fit: boolean;
    @Input() tab_index: number;
    @Input() placeholder: string;
    @Output() constant_changed: EventEmitter<any> = new EventEmitter();

    constructor(private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.app_fit_content = this.app_fit_content ?? this.modelValue;

        if (!this.allow_fit) {
            this.allow_fit = this.formats?.allow_fit ?? false;
        }
        if (!this.label) {
            this.classes += ' no-label';
        }
        // filter_compare and stringFunction are plain inputs with no setters, so a template
        // that binds either of them after [modelValue] and [options] leaves both setters'
        // orphan-option computation stale. ngOnInit runs after every input is assigned.
        this.updateOrphanedOptions();
        this.changeDetectorRef.markForCheck();
    }

    onOpen(value) {
        this.isOpen = value;
        if (value && this.filterSelect?.panel?.nativeElement) {
            this.filterSelect.panel.nativeElement.scrollTop = 0;
        }
        this.filteredOptions = this.options;
        this.filterValue = "";
    }

    filterOptionList(filterString: string): void {
        this.filterValue = filterString?.toLowerCase();
        this.filteredOptions = allStringFilter(filterString, this.options);
        if (this.filteredOptions.length === 0) {
            this.errorMessage = `${this.filterValue} does not exist`;
        }
        this.changeDetectorRef.markForCheck();
    }

    onClear() {
        this.filterValue = "";
        this.filteredOptions = allStringFilter("", this.options);
    }

    emitConstantChangedComplete(event) {
        const selected = event;
        const hiddenSelected = this.modelValue?.filter(value => {
            return !allStringFilter(String(this.valueFunction(value)), this.filteredOptions, null, null, this.valueFunction)?.length
        }) || [];
        this.modelValue = [...new Set([...selected, ...hiddenSelected])];
        this.constant_changed.emit(this.modelValue);
        this.changeDetectorRef.markForCheck();
    }

    compareByName(option, value): boolean {
        if (value) {
            return option === value;
        }
        return false;
    }

    getTooltip() {
        const stringValues = (this.modelValue?.map(value => {
            return this.stringFunction(value);
        }));
        return stringValues?.length ? stringValues.join(", ") : "No items selected";
    }

    getPlaceholder(): string {
        if (!this.modelValue?.length) {
            return this.placeholder ? this.placeholder : this.disabled ? '' : `Click to select ${this.listType || ''}`
        }
        let firstValue = this.stringFunction ? this.stringFunction(this.modelValue?.[0]) : null;
        if (firstValue && (isGuid(firstValue) || isInteger(firstValue))) {
            let firstOption = this.options.find(option => option == firstValue || option.id == firstValue || option.value == firstValue);
            firstValue = firstOption ? this.stringFunction(firstOption) : null;
        }

        if (this.modelValue?.length === 1) {
            return firstValue || `(1 ${this.listType || ''} selected)`
        } else {
            return firstValue ? `${firstValue} (+${this.modelValue.length - 1})` : `(${this.modelValue.length} ${this.listType || ''} selected)`
        }
    }

    /**
     * Selected values that no option can display are rendered as marked options, so the open
     * panel shows which of the selected values are retired. Matched against the unfiltered
     * list, so a search term cannot manufacture one.
     */
    private updateOrphanedOptions(): void {
        const values: any[] = Array.isArray(this._modelValue) ? this._modelValue : [];
        this.orphanedOptions = values
            .map(value => findOrphanedOption(
                value, this._options, [], this.filter_compare || this.compareByName, this.stringFunction))
            .filter(orphan => !!orphan);
    }
}
